import {
  DeleteOutlined,
  DownloadOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import {
  Form,
  Switch,
  Typography,
  Upload,
  Button,
  Space,
  Spin,
  Tooltip,
} from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useAttachFilesMutation,
  useRemoveFilesMutation,
  useGetFilesMutation,
  type ModelClientReminder,
  type ModelFile,
} from '@app/api/ClientReminders';
import { ExtendedMessage } from '@app/components';
import { PREFIX } from '@app/constants';
import { FRONT_WARNING, SUCCESS } from '@app/constants/alerts';
import { useUserPrint } from '@app/hooks';
import { LabelWithTime } from '@app/pages/Notifications/components/LabelWithTime';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import type { FormInstance } from 'antd';
import type { RcFile, UploadProps } from 'antd/es/upload/interface';

import '@pages/Notifications/styles/ClientReminderComponent.desktop.scss';

const { Title, Text } = Typography;
const { Item } = Form;
const { Dragger } = Upload;

const COMPONENT_CLASS_NAME = `${PREFIX}client-reminder-component`;

type ClientReminderComponentProps = {
  form: FormInstance;
  title: string;
  description: string;
  type: 'before' | 'after';
  reminder: ModelClientReminder | null;
};

const MAX_FILE_SIZE_BYTES = 300 * 1024 * 1024;

const ALLOWED_EXTENSIONS: string[] = [
  'xlsx',
  'xls',
  'jpg',
  'png',
  'jpeg',
  'pdf',
  'doc',
  'docx',
  'txt',
  'mp4',
  'webp',
];

const ACCEPTED_FILE_TYPES = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(
  ','
);

const readFileAsBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        const parts = reader.result.split(',');
        const base64 = parts.length > 1 ? parts[1] : parts[0];
        resolve(base64);
      } else {
        reject(new Error('Не удалось прочитать файл'));
      }
    };

    reader.onerror = () => {
      if (reader.error) {
        reject(reader.error);
      } else {
        reject(new Error('Не удалось прочитать файл'));
      }
    };

    reader.readAsDataURL(file);
  });

export const ClientReminderComponent: React.FC<
  ClientReminderComponentProps
> = ({ form, title, description, type, reminder }) => {
  const hoursValue = form.getFieldValue(`${type}BirthdayHours`) || 0;
  const minutesValue = form.getFieldValue(`${type}BirthdayMinutes`) || 0;

  const handleHoursChange = (value: number) => {
    form.setFieldsValue({ [`${type}BirthdayHours`]: value });
  };

  const handleMinutesChange = (value: number) => {
    form.setFieldsValue({ [`${type}BirthdayMinutes`]: value });
  };

  const isFeedbackStatus = useWatch(`${type}BirthdayStatus`, form);

  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const [files, setFiles] = useState<ModelFile[]>([]);

  const [attachFiles, { isLoading: isAttachLoading }] =
    useAttachFilesMutation();
  const [removeFiles, { isLoading: isRemoveLoading }] =
    useRemoveFilesMutation();
  const [getFiles, { isLoading: isGetFilesLoading }] = useGetFilesMutation();

  const isFilesLoading =
    isAttachLoading || isRemoveLoading || isGetFilesLoading;

  const hasReminderContext = Boolean(reminder?.Id && ActiveBranchId);

  const fetchFiles = useCallback(async () => {
    if (!hasReminderContext || !reminder) {
      setFiles([]);
      return;
    }

    try {
      const response = await getFiles({
        ActiveBranchId,
        data: { Id: reminder.Id, FileIds: null },
      }).unwrap();

      setFiles(response.Files ?? []);
    } catch (error) {
      showNotification(error);
    }
  }, [
    ActiveBranchId,
    getFiles,
    hasReminderContext,
    reminder,
    showNotification,
  ]);

  useEffect(() => {
    if (
      !hasReminderContext ||
      !reminder?.FileIds ||
      reminder.FileIds.length === 0
    ) {
      setFiles([]);
      return;
    }

    void fetchFiles();
  }, [fetchFiles, hasReminderContext, reminder?.FileIds]);

  const handleDownload = useCallback(
    (file: ModelFile) => {
      if (!file.Data) {
        showNotification('Файл недоступен для скачивания', FRONT_WARNING);
        return;
      }

      try {
        const binaryString = window.atob(file.Data);
        const { length } = binaryString;
        const bytes = new Uint8Array(length);

        for (let i = 0; i < length; i += 1) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const blob = new Blob([bytes], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = file.Name ?? 'file';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        showNotification(
          'Произошла ошибка при скачивании файла',
          FRONT_WARNING
        );
      }
    },
    [showNotification]
  );

  const handleRemoveFile = useCallback(
    async (fileId: number | undefined) => {
      if (!hasReminderContext || !reminder || !fileId) {
        return;
      }

      try {
        await removeFiles({
          ActiveBranchId,
          data: { Id: reminder.Id, FileIds: [fileId] },
        }).unwrap();

        await fetchFiles();

        showNotification('Файл удалён из уведомления', SUCCESS);
      } catch (error) {
        showNotification(error);
      }
    },
    [
      ActiveBranchId,
      fetchFiles,
      hasReminderContext,
      reminder,
      removeFiles,
      showNotification,
    ]
  );

  const handleAttachFile = useCallback(
    async (file: RcFile) => {
      if (!hasReminderContext || !reminder) {
        showNotification(
          'Не удалось определить уведомление для прикрепления файлов',
          FRONT_WARNING
        );
        return;
      }

      try {
        const base64 = await readFileAsBase64(file);

        const fileModel: ModelFile = {
          Id: 0,
          Name: file.name,
          Data: base64,
          Description: null,
          Lifetime: null,
        };

        await attachFiles({
          ActiveBranchId,
          data: { Id: reminder.Id, Files: [fileModel] },
        }).unwrap();

        await fetchFiles();

        showNotification('Файл прикреплён к уведомлению', SUCCESS);
      } catch (error) {
        showNotification(error);
      }
    },
    [
      ActiveBranchId,
      attachFiles,
      fetchFiles,
      hasReminderContext,
      reminder,
      showNotification,
    ]
  );

  const uploadProps: UploadProps = useMemo(
    () => ({
      name: 'file',
      multiple: true,
      accept: ACCEPTED_FILE_TYPES,
      showUploadList: false,
      beforeUpload: (file) => {
        const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

        if (!ALLOWED_EXTENSIONS.includes(extension)) {
          showNotification(
            'Недопустимый формат файла. Разрешены: xlsx, xls, jpg, png, jpeg, pdf, doc, docx, txt, mp4, webp',
            FRONT_WARNING
          );
          return Upload.LIST_IGNORE;
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
          showNotification('Максимальный размер файла — 300 МБ', FRONT_WARNING);
          return Upload.LIST_IGNORE;
        }

        void handleAttachFile(file as RcFile);
        return false;
      },
      disabled: isFilesLoading || !hasReminderContext,
    }),
    [handleAttachFile, hasReminderContext, isFilesLoading, showNotification]
  );

  return (
    <div className={COMPONENT_CLASS_NAME}>
      <div className={`${COMPONENT_CLASS_NAME}__header-row`}>
        <Title level={4} className={`${COMPONENT_CLASS_NAME}__header`}>
          {title}
        </Title>
        <div className={`${COMPONENT_CLASS_NAME}__switch-wrap`}>
          <Form.Item
            name={`${type}BirthdayStatus`}
            className={`${COMPONENT_CLASS_NAME}__switch`}
          >
            <Switch />
          </Form.Item>
        </div>
      </div>

      {isFeedbackStatus && (
        <>
          <div className={`${COMPONENT_CLASS_NAME}__description`}>
            {description}
          </div>

          <LabelWithTime
            componentClassName={COMPONENT_CLASS_NAME}
            type={type}
            daysFormItemName={`${type}BirthdayDays`}
            hoursFormItemName={`${type}BirthdayHours`}
            minutesFormItemName={`${type}BirthdayMinutes`}
            onHoursChange={handleHoursChange}
            onMinutesChange={handleMinutesChange}
            hoursValue={hoursValue}
            minutesValue={minutesValue}
            withExtendedHours
            showDays
          />

          <Text type="secondary">
            День рождения клиента наступает в момент начала суток (00:00)
          </Text>

          <div className={`${COMPONENT_CLASS_NAME}__label`}>
            Текст уведомления
          </div>
          <ExtendedMessage
            form={form}
            socialType="whatsapp"
            formItemName={`${type}BirthdayMessage`}
            formItemClassName={`${COMPONENT_CLASS_NAME}__textarea`}
          />

          <Text className={`${COMPONENT_CLASS_NAME}__label`}>
            Файлы уведомления
          </Text>
          <Item
            className={`${COMPONENT_CLASS_NAME}__files-upload`}
            extra="Максимальный размер файла — 300 МБ. Допустимые форматы: xlsx, xls, jpg, png, jpeg, pdf, doc, docx, txt, mp4, webp."
          >
            <Spin spinning={isFilesLoading}>
              <Dragger {...uploadProps}>
                <Space direction="vertical">
                  <PaperClipOutlined />
                  <span>Перетащите файлы сюда или нажмите, чтобы выбрать</span>
                </Space>
              </Dragger>
            </Spin>
          </Item>

          {files.length > 0 && (
            <div className={`${COMPONENT_CLASS_NAME}__files-list`}>
              {files.map((file) => (
                <div
                  key={file.Id ?? file.Name}
                  className={`${COMPONENT_CLASS_NAME}__file-item`}
                >
                  <span
                    className={`${COMPONENT_CLASS_NAME}__file-name`}
                    title={file.Name ?? undefined}
                  >
                    {file.Name}
                  </span>
                  <Space size={4}>
                    <Tooltip title="Скачать файл">
                      <Button
                        type="text"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownload(file)}
                        disabled={!file.Data}
                      />
                    </Tooltip>
                    <Tooltip title="Удалить файл">
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveFile(file.Id)}
                      />
                    </Tooltip>
                  </Space>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
